import React, { useMemo, useState } from "react";
import { Calendar, UtensilsCrossed, Wine, Music, Ship, ArrowRight, ChevronDown } from "lucide-react";
import { CRUZEIRO_CABINES, DEFAULT_CABINE, cabineLabel } from "../lib/cruzeiroCabines";

// Formata "12x de R$ X,YZ" com centavos e sem juros.
const formatParcela = (valor) => {
  if (valor == null || Number.isNaN(Number(valor))) return null;
  try {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(valor));
  } catch {
    return `R$ ${Number(valor).toFixed(2)}`;
  }
};

/** Card único da vitrine /cruzeiros. Cada instância tem seu próprio estado
 *  de cabine selecionada (não afeta os outros cards). Segue o layout
 *  do protótipo teste-card-capa-v3.html: badge de opções + data,
 *  ícones de inclusos, preço em 12x, dropdown de cabine, botão que muda
 *  conforme a cabine, painel expansível de detalhes, lightbox na foto. */
export default function CruzeiroCard({ destaque: d, onCheckout, onOpenLightbox }) {
  // "interna_2" (Dupla Interna 2 Pessoas) vem pré-selecionada.
  const [selectedCabine, setSelectedCabine] = useState(DEFAULT_CABINE);
  const [detOpen, setDetOpen] = useState(false);

  const cabineInfo = d.cabines?.[selectedCabine];
  const hasLink = !!cabineInfo?.link;
  const parcelaTxt = useMemo(() => {
    if (!hasLink || cabineInfo?.preco == null) {
      // Se a cabine selecionada não tem preço próprio, mostra o mínimo do card.
      return formatParcela(d.preco_minimo_parcelado);
    }
    return formatParcela(Number(cabineInfo.preco) / 12);
  }, [hasLink, cabineInfo, d.preco_minimo_parcelado]);

  const cabineNome = cabineLabel(selectedCabine);
  const cabineM2 = cabineInfo?.m2 || "—";
  const opcoes = d.opcoes_count || 0;

  return (
    <article className="cruz-card group" data-testid={`cruz-card-${d.id}`}>
      <div
        className="cruz-card-img cruz-card-img-clickable"
        onClick={() => d.image_url && onOpenLightbox(d.image_url)}
        role={d.image_url ? "button" : undefined}
        aria-label="Ampliar foto"
        data-testid={`cruz-card-photo-${d.id}`}
      >
        {d.image_url ? (
          <img src={d.image_url} alt={d.titulo} loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30 bg-white/5">
            <Ship size={48} />
          </div>
        )}
        {opcoes > 0 && (
          <span className="cruz-card-badge-opcoes" data-testid={`cruz-badge-opcoes-${d.id}`}>
            {opcoes} {opcoes === 1 ? "opção" : "opções"} de cabine
          </span>
        )}
        {parcelaTxt && (
          <span className="cruz-card-badge-preco" data-testid={`cruz-preco-${d.id}`}>
            A partir de 12x de
            <b>{parcelaTxt}</b>
            <small>por pessoa</small>
          </span>
        )}
        {d.data_resumo && (
          <span className="cruz-card-badge-data">
            <Calendar size={11} /> {d.data_resumo}
          </span>
        )}
      </div>

      <div className="cruz-card-body">
        <h3 className="cruz-card-title">{d.titulo}</h3>
        {(d.nome_navio || d.noites) && (
          <p className="cruz-card-meta">
            {d.noites ? `${d.noites} ${d.noites === 1 ? "noite" : "noites"}` : ""}
            {d.noites && d.nome_navio ? " · " : ""}
            {d.nome_navio}
          </p>
        )}

        {/* Linha de inclusos — ícones laranja quando incluso, cinza quando à parte */}
        <div className="cruz-card-inclusos">
          <div className={`cruz-card-incluso ${d.refeicoes ? "" : "off"}`}>
            <UtensilsCrossed size={16} />
            <span>Refeições<small>{d.refeicoes ? "Inclusas" : "À parte"}</small></span>
          </div>
          <div className={`cruz-card-incluso ${d.bebidas ? "" : "off"}`}>
            <Wine size={16} />
            <span>Bebidas<small>{d.bebidas ? "Inclusas" : "À parte"}</small></span>
          </div>
          <div className={`cruz-card-incluso ${d.show ? "" : "off"}`}>
            <Music size={16} />
            <span>{d.show ? "Show" : "Entretenimento"}<small>{d.show ? "Line-up incluso" : "A bordo"}</small></span>
          </div>
        </div>

        {/* Dropdown de cabine — independente por card */}
        <select
          className="cruz-card-select"
          value={selectedCabine}
          onChange={(e) => setSelectedCabine(e.target.value)}
          data-testid={`cruz-select-${d.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {CRUZEIRO_CABINES.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>

        {hasLink ? (
          <button
            type="button"
            onClick={() => onCheckout(cabineInfo.link)}
            className="cruz-card-cta"
            data-testid={`cruz-btn-pagar-${d.id}`}
          >
            Disponível para Compra <ArrowRight size={14} />
          </button>
        ) : (
          <button
            type="button"
            disabled
            className="cruz-card-cta cruz-card-cta-disabled"
            data-testid={`cruz-btn-indisponivel-${d.id}`}
          >
            Indisponível nesta cabine
          </button>
        )}
        <div className="cruz-card-categoria" data-testid={`cruz-categoria-${d.id}`}>
          Categoria selecionada: <b>{cabineNome}</b>
        </div>

        {/* Painel expansível — atualiza conforme a cabine selecionada */}
        <button
          type="button"
          className="cruz-card-detalhes-toggle"
          onClick={() => setDetOpen((v) => !v)}
          data-testid={`cruz-detalhes-toggle-${d.id}`}
        >
          {detOpen ? "Ocultar Detalhes" : "Ver Detalhes"}
          <ChevronDown size={12} className={`transition-transform ${detOpen ? "rotate-180" : ""}`} />
        </button>
        <div
          className={`cruz-card-detalhes-painel ${detOpen ? "open" : ""}`}
          data-testid={`cruz-detalhes-${d.id}`}
        >
          {d.embarque_data && (
            <div className="cruz-card-detalhes-linha">
              <span>Embarque</span>
              <b>{d.embarque_data}{d.embarque_horario ? ` · ${d.embarque_horario}` : ""}</b>
            </div>
          )}
          {d.desembarque_data && (
            <div className="cruz-card-detalhes-linha">
              <span>Desembarque</span>
              <b>{d.desembarque_data}{d.desembarque_horario ? ` · ${d.desembarque_horario}` : ""}</b>
            </div>
          )}
          {d.noites > 0 && (
            <div className="cruz-card-detalhes-linha">
              <span>Duração</span>
              <b>{d.noites} {d.noites === 1 ? "noite" : "noites"}</b>
            </div>
          )}
          <div className="cruz-card-detalhes-linha">
            <span>Cabine selecionada</span>
            <b>{cabineNome}</b>
          </div>
          <div className="cruz-card-detalhes-linha cruz-card-detalhes-linha-last">
            <span>Tamanho da cabine</span>
            <b>{cabineM2}</b>
          </div>
        </div>
      </div>
    </article>
  );
}
